/**
 * The one mapping from an API failure to what a screen shows (DNKADM-0440).
 *
 *  - `401` → `session`: the client has already tried its one silent refresh and
 *    signed out (`signOut({ expired: true })`), so the screen only offers the
 *    existing centralized sign-in; it never retries the call itself.
 *  - `403` → `forbidden`: an authorization answer, never a network failure — the
 *    DNKADM-0433 access notice (why and who to ask), and no Try again.
 *  - `404` → `notFound`.
 *  - `400`/`409`/`422` → `rejected`: the server's own (brand-scrubbed) message.
 *  - anything else (`5xx`, `429`, a dropped connection, a non-`ApiError`) → `retry`.
 *
 * Per-feature mappers may only add feature-specific codes on top of this (e.g. a
 * money or access `409` business code); they never re-map the statuses above.
 * `ScreenError` (`src/components/ScreenError.tsx`) renders the screen state;
 * `classifyFormError` / `actionErrorMessage` give a write the same rules.
 */
import { ApiError } from '@/api/errors';
import { serverMessage } from '@/api/serverMessage';
import {
  ACCESS_DENIED_FALLBACK,
  ACCESS_DENIED_TITLE,
  SESSION_EXPIRED_MESSAGE,
} from '@/components/accessCopy';

export type ScreenErrorKind = 'session' | 'forbidden' | 'notFound' | 'rejected' | 'retry';

export interface ScreenErrorState {
  kind: ScreenErrorKind;
  /** The server's brand-scrubbed message, or `null` when it sent none. */
  message: string | null;
}

/** Map any thrown failure to the screen state it should render. */
export function toScreenError(error: unknown): ScreenErrorState {
  const message = serverMessage(error);
  if (!(error instanceof ApiError)) return { kind: 'retry', message };
  if (error.code === 'SESSION_EXPIRED' || error.status === 401) return { kind: 'session', message };
  if (error.code === 'FORBIDDEN' || error.status === 403) return { kind: 'forbidden', message };
  if (error.code === 'NOT_FOUND' || error.status === 404) return { kind: 'notFound', message };
  if (
    error.code === 'VALIDATION' ||
    error.code === 'CONFLICT' ||
    error.status === 400 ||
    error.status === 409 ||
    error.status === 422
  ) {
    return { kind: 'rejected', message };
  }
  return { kind: 'retry', message };
}

/**
 * The one-line message for a failed ACTION (a save, a toast): the session line on a
 * `401`, the access line on a `403` (never "try again" — a retry cannot work), and
 * otherwise the server's message, then `fallback`.
 */
export function actionErrorMessage(error: unknown, fallback: string): string {
  const state = toScreenError(error);
  if (state.kind === 'session') return SESSION_EXPIRED_MESSAGE;
  if (state.kind === 'forbidden') {
    return state.message ?? `${ACCESS_DENIED_TITLE}. ${ACCESS_DENIED_FALLBACK}`;
  }
  return state.message ?? fallback;
}

export type FormErrorView =
  /** Render on the form or dialog — the operator can correct and retry. */
  | { kind: 'inline'; message: string }
  /** Raise a non-blocking error toast — not a field-level fix. */
  | { kind: 'toast'; message: string };

export interface FormErrorOptions {
  /** Copy for the toast when the server sends no message (default: `inlineFallback`). */
  toastFallback?: string;
  /**
   * Whether a `409` surfaces inline beside a `400` (default `true`). A form whose `409`
   * is a state change rather than a field fix (e.g. a locked setting) passes `false` and
   * keeps it in the toast — the message is the server's either way.
   */
  conflictInline?: boolean;
}

/**
 * Classify a failed form write: a `400` (and a `409` unless `conflictInline` is off)
 * renders inline with the server's message ?? `inlineFallback`; everything else —
 * including a `422`, which the folded per-feature classifiers toasted — is a toast via
 * {@link actionErrorMessage} (the server's message ?? `toastFallback`; only a `401`/`403`
 * reads differently, DNKADM-0440).
 */
export function classifyFormError(
  error: unknown,
  inlineFallback: string,
  { toastFallback = inlineFallback, conflictInline = true }: FormErrorOptions = {},
): FormErrorView {
  const inline =
    error instanceof ApiError &&
    (error.code === 'VALIDATION' || (conflictInline && error.code === 'CONFLICT'));
  if (inline) return { kind: 'inline', message: serverMessage(error) ?? inlineFallback };
  return { kind: 'toast', message: actionErrorMessage(error, toastFallback) };
}
